import { useState, useMemo } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Trans, useLingui } from '@lingui/react/macro';
import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Skeleton,
} from '@pantry/ui';
import {
  ArrowLeftIcon,
  PinIcon,
  PinOffIcon,
  ArchiveIcon,
  ArchiveRestoreIcon,
  Trash2Icon,
  PencilIcon,
} from 'lucide-react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {
  useShoppingList,
  useUpdateShoppingList,
  useDeleteShoppingList,
  useReorderShoppingListItems,
  useFeatureAccess,
} from '@pantry/api-client';
import { FeatureFlag } from '@pantry/shared';
import { toast } from 'sonner';
import { AnalyticsEvents } from '@pantry/analytics';
import { useSelectedHousehold } from '../../contexts/HouseholdContext';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import { useAnalytics } from '../../hooks/useAnalytics';
import { ShoppingListItemRow } from '../../components/shopping-lists/ShoppingListItemRow';
import { AddItemForm } from '../../components/shopping-lists/AddItemForm';
import { BatchActionsBar } from '../../components/shopping-lists/BatchActionsBar';
import { EmptyState } from '../../components/shopping-lists/EmptyState';
import { EditListDialog } from '../../components/shopping-lists/EditListDialog';
import { DeleteListDialog } from '../../components/shopping-lists/DeleteListDialog';
import { TripScannerSection } from '../../components/shopping-lists/TripScannerSection';
import { TripCompletionDialog } from '../../components/shopping-lists/TripCompletionDialog';
import { UpgradeDialog } from '../../components/premium/UpgradeDialog';

export const Route = createFileRoute('/_app/shopping-lists_/$listId')({
  component: ShoppingListDetailPage,
});

function ShoppingListDetailPage() {
  const { t } = useLingui();
  const { listId } = Route.useParams();
  const navigate = useNavigate();
  const { selectedHousehold } = useSelectedHousehold();
  const householdId = selectedHousehold?.id ?? '';

  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false);
  const [completionTripId, setCompletionTripId] = useState<string | null>(null);

  const { data: list, isLoading, error, refetch } = useShoppingList(householdId, listId);

  const updateList = useUpdateShoppingList();
  const deleteList = useDeleteShoppingList();
  const reorderItems = useReorderShoppingListItems();

  const flagEnabled = useFeatureFlag(FeatureFlag.SHOPPING_LISTS_PREMIUM);
  const { hasAccess: hasPremium } = useFeatureAccess(flagEnabled);
  const { track } = useAnalytics();

  // DnD sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  // Sort: unchecked by sortOrder, then checked at bottom
  const uncheckedItems = useMemo(
    () =>
      [...(list?.items ?? [])]
        .filter((i) => !i.isChecked)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [list?.items]
  );
  const checkedItems = useMemo(
    () =>
      [...(list?.items ?? [])].filter((i) => i.isChecked).sort((a, b) => a.sortOrder - b.sortOrder),
    [list?.items]
  );

  if (!householdId || isLoading) {
    return <ShoppingListDetailSkeleton />;
  }

  if (error || !list) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center gap-4">
        <p className="text-muted-foreground">
          <Trans>Something went wrong loading this list.</Trans>
        </p>
        <Button variant="outline" onClick={() => void refetch()}>
          <Trans context="action_button">Retry</Trans>
        </Button>
      </div>
    );
  }

  const sortedItems = [...uncheckedItems, ...checkedItems];
  const hasCheckedItems = checkedItems.length > 0;
  const hasItems = list.items.length > 0;
  const allChecked = list.itemCount > 0 && list.checkedCount === list.itemCount;

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = uncheckedItems.findIndex((i) => i.id === active.id);
    const newIndex = uncheckedItems.findIndex((i) => i.id === over.id);

    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = [...uncheckedItems];
    const [moved] = reordered.splice(oldIndex, 1);
    reordered.splice(newIndex, 0, moved);

    const itemIds = [...reordered.map((i) => i.id), ...checkedItems.map((i) => i.id)];

    track(AnalyticsEvents.SHOPPING_LIST_REORDERED, { householdId, listId });
    reorderItems.mutate(
      { householdId, listId, data: { itemIds } },
      {
        onError: () => {
          toast.error(t`Failed to update. Please try again.`);
        },
      }
    );
  };

  const handlePin = () => {
    if (!hasPremium) {
      track(AnalyticsEvents.SHOPPING_LIST_UPGRADE_PROMPTED, { householdId, trigger: 'pin' });
      setShowUpgradeDialog(true);
      return;
    }
    track(AnalyticsEvents.SHOPPING_LIST_PINNED, { householdId, listId });
    void updateList.mutateAsync({
      householdId,
      listId,
      data: { isPinned: !list.isPinned },
    });
  };

  const handleArchiveClick = () => {
    const hasUnchecked = list.checkedCount < list.itemCount;
    if (list.isActive && hasUnchecked) {
      setShowArchiveConfirm(true);
    } else {
      performArchive();
    }
  };

  const performArchive = async () => {
    if (list.isActive) {
      track(AnalyticsEvents.SHOPPING_LIST_ARCHIVED, {
        householdId,
        listId,
        itemCount: list.itemCount,
        checkedCount: list.checkedCount,
      });
    }
    try {
      await updateList.mutateAsync({ householdId, listId, data: { isActive: !list.isActive } });
      void navigate({ to: '/shopping-lists' });
    } catch {
      toast.error(t`Failed to archive. Please try again.`);
    }
  };

  const handleDeleteConfirm = async () => {
    track(AnalyticsEvents.SHOPPING_LIST_DELETED, { householdId, listId });
    try {
      await deleteList.mutateAsync({ householdId, listId });
      setShowDeleteDialog(false);
      void navigate({ to: '/shopping-lists' });
    } catch {
      toast.error(t`Failed to delete. Please try again.`);
    }
  };

  return (
    <div className="space-y-6 pb-8 max-w-2xl mx-auto">
      {/* Back + Header */}
      <div>
        <Link
          to="/shopping-lists"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          <Trans>Shopping Lists</Trans>
        </Link>

        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {list.isPinned && (
              <PinIcon className="h-4 w-4 text-primary shrink-0" aria-label={t`Pinned`} />
            )}
            <h1 className="font-display text-2xl font-semibold text-foreground truncate">
              {list.name}
            </h1>
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7 shrink-0"
              onClick={() => setShowEditDialog(true)}
              aria-label={t`Rename list`}
            >
              <PencilIcon className="h-3.5 w-3.5" />
            </Button>
          </div>

          <div className="flex gap-1 shrink-0">
            <Button
              variant="ghost"
              size="icon"
              onClick={handlePin}
              aria-label={list.isPinned ? t`Unpin list` : t`Pin list`}
              title={
                list.isPinned
                  ? t({ message: 'Unpin', context: 'action_button' })
                  : t({ message: 'Pin', context: 'action_button' })
              }
            >
              {list.isPinned ? <PinOffIcon className="h-4 w-4" /> : <PinIcon className="h-4 w-4" />}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              onClick={handleArchiveClick}
              aria-label={
                list.isActive
                  ? t({ message: 'Archive', context: 'action_button' })
                  : t({ message: 'Unarchive', context: 'action_button' })
              }
              title={
                list.isActive
                  ? t({ message: 'Archive', context: 'action_button' })
                  : t({ message: 'Unarchive', context: 'action_button' })
              }
            >
              {list.isActive ? (
                <ArchiveIcon className="h-4 w-4" />
              ) : (
                <ArchiveRestoreIcon className="h-4 w-4" />
              )}
            </Button>

            <Button
              variant="ghost"
              size="icon"
              className="text-destructive hover:text-destructive"
              onClick={() => setShowDeleteDialog(true)}
              aria-label={t`Delete list`}
            >
              <Trash2Icon className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Progress text */}
        <p className="text-sm text-muted-foreground mt-1">
          {list.itemCount === 0
            ? t`No items`
            : t`${list.checkedCount} of ${list.itemCount} items checked`}
          {allChecked && (
            <span className="ml-2 text-consumed font-medium">
              <Trans>All done!</Trans>
            </span>
          )}
        </p>
      </div>

      {/* Trip Scanner Section */}
      <TripScannerSection
        householdId={householdId}
        listId={listId}
        onCompleteTrip={(tripId) => setCompletionTripId(tripId)}
      />

      {/* Batch Actions */}
      {hasItems && (
        <BatchActionsBar
          householdId={householdId}
          listId={listId}
          hasCheckedItems={hasCheckedItems}
          hasItems={hasItems}
          hasPremium={hasPremium}
        />
      )}

      {/* Add Item Form */}
      <AddItemForm householdId={householdId} listId={listId} />

      {/* Item List */}
      {sortedItems.length === 0 ? (
        <EmptyState variant="no-items" />
      ) : (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={uncheckedItems.map((i) => i.id)}
            strategy={verticalListSortingStrategy}
          >
            <div className="space-y-2">
              {uncheckedItems.map((item) => (
                <ShoppingListItemRow
                  key={item.id}
                  item={item}
                  householdId={householdId}
                  listId={listId}
                />
              ))}
            </div>
          </SortableContext>

          {checkedItems.length > 0 && (
            <div className="space-y-2 mt-4">
              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider px-1">
                <Trans>Checked</Trans>
              </p>
              {checkedItems.map((item) => (
                <ShoppingListItemRow
                  key={item.id}
                  item={item}
                  householdId={householdId}
                  listId={listId}
                />
              ))}
            </div>
          )}
        </DndContext>
      )}

      {/* Dialogs */}
      <EditListDialog
        householdId={householdId}
        listId={listId}
        currentName={list.name}
        open={showEditDialog}
        onClose={() => setShowEditDialog(false)}
      />

      <DeleteListDialog
        open={showDeleteDialog}
        listName={list.name}
        isPending={deleteList.isPending}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteConfirm}
      />

      {/* Archive confirmation dialog */}
      <Dialog
        open={showArchiveConfirm}
        onOpenChange={(open) => !open && setShowArchiveConfirm(false)}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Trans context="dialog_title">Archive List?</Trans>
            </DialogTitle>
            <DialogDescription>
              <Trans>This list has unchecked items. Archive anyway?</Trans>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowArchiveConfirm(false)}>
              <Trans context="action_button">Cancel</Trans>
            </Button>
            <Button
              onClick={() => {
                setShowArchiveConfirm(false);
                void performArchive();
              }}
            >
              <Trans context="action_button">Archive</Trans>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {completionTripId && (
        <TripCompletionDialog
          householdId={householdId}
          listId={listId}
          tripId={completionTripId}
          listName={list.name}
          open={!!completionTripId}
          onOpenChange={(open) => {
            if (!open) setCompletionTripId(null);
          }}
        />
      )}

      <UpgradeDialog
        open={showUpgradeDialog}
        onClose={() => setShowUpgradeDialog(false)}
        feature={t`Pin Shopping Lists`}
        description={t`Pinned lists always appear at the top for quick access.`}
        onUpgrade={() => void navigate({ to: '/upgrade' })}
      />
    </div>
  );
}

function ShoppingListDetailSkeleton() {
  return (
    <div className="space-y-6 pb-8 max-w-2xl mx-auto">
      <div>
        <Skeleton className="h-4 w-32 mb-4" />
        <Skeleton className="h-8 w-48 mb-2" />
        <Skeleton className="h-4 w-32" />
      </div>
      <div className="flex gap-2">
        <Skeleton className="h-8 w-28" />
        <Skeleton className="h-8 w-32" />
        <Skeleton className="h-8 w-24" />
      </div>
      <Skeleton className="h-10 w-full" />
      <div className="space-y-2">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-14 w-full rounded-lg" />
        ))}
      </div>
    </div>
  );
}
